import { DeleteOutlined, PlusOutlined, SwapOutlined } from "@ant-design/icons";
import { useTable } from "@refinedev/antd";
import { useInvalidate } from "@refinedev/core";
import {
  Badge,
  Button,
  Flex,
  Popconfirm,
  Table,
  TableColumnsType,
  Tag,
  Typography,
  message
} from "antd";
import apiHelper from "api/apiHelper";
import { cancelJob, deleteJob as deleteJobById } from "api/job";
import { LoadingSpinner } from "components/LoadingSpinner";
import { getPaginationProps } from "components/PaginationProps";
import { ViewJob } from "components/Panel/ConsumerPanel/JobsOverview/ViewJob";
import { SearchComponent } from "components/SearchComponent";
import { JobStatus } from "constants/common";
import { STATUS_MAP, STATUS_MAP_COLOR } from "constants/statusUtils";
import { CreateJobModal } from "modals/CreateJobModal";
import { UserInfoModal } from "modals/UserInfoModal";
import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { COLOR_PRIMARY_ORANGE } from "theme/colors";
import { User } from "../../types/props";
import { rescaleJobRunningPercentage } from "utils/rescaleJobRunningPercentage";
import { useCustomLiveEvent } from "hooks/use-custom-live-event.hook";

export const JobsOverview: React.FC = () => {
  const { tableProps, setFilters, filters, tableQueryResult } = useTable({
    liveMode: "auto",
    resource: "jobs/admin",
    pagination: {
      mode: "server"
    },
    syncWithLocation: true,
    sorters: {
      initial: [
        {
          field: "createdAt",
          order: "desc"
        }
      ]
    }
  });
  const invalidate = useInvalidate();
  const [searchParams, setSearchParams] = useSearchParams();
  const jobId = searchParams.get("jobId") ?? "";

  const [tableData, setTableData] = useState<any>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<("BS" | "CU")[]>([]);
  const [createJobModal, setCreateJobModal] = useState(false);
  const [userModal, setUserModal] = useState<{
    show: boolean;
    user: User | undefined;
  }>({
    show: false,
    user: undefined
  });

  const cancel = async (jobId: string) => {
    await cancelJob(jobId);
    invalidate({
      resource: "jobs/admin",
      invalidates: ["resourceAll"]
    });
  };

  const deleteJob = async (jobId: string) => {
    await deleteJobById(jobId);
    invalidate({
      resource: "jobs/admin",
      invalidates: ["resourceAll"]
    });
  };

  const changePriority = async (id: string, priority: number) => {
    try {
      await apiHelper.patch(`/jobs/change-priority/${id}`, {
        priority: priority
      });
      invalidate({
        resource: "jobs/admin",
        invalidates: ["resourceAll"]
      });
    } catch (er) {
      // console.log(er);
      message.error("Failed to change the priority");
    }
  };

  const columns: TableColumnsType<any> = [
    {
      title: "Job Name",
      dataIndex: "job_name",
      key: "jobName",
      align: "center",
      width: 140,
      ellipsis: true
    },
    {
      title: "User",
      dataIndex: "user.email",
      key: "user",
      align: "center",
      width: 140,
      ellipsis: true,
      render(_, record) {
        return (
          <Typography.Link
            ellipsis
            style={{ width: 140 }}
            onClick={() => {
              setUserModal({
                show: true,
                user: record.user
              });
            }}>
            {record.user.email}
          </Typography.Link>
        );
      }
    },
    {
      title: <div style={{ textWrap: "nowrap" }}>Job Type</div>,
      dataIndex: "job_type",
      key: "jobType",
      align: "center",
      width: 30,
      filters: [
        {
          text: "BS",
          value: "BS"
        },
        {
          text: "CU",
          value: "CU"
        }
      ],
      onFilter(value, record) {
        return value === record.job_type;
      }
    },
    {
      title: <div style={{ textWrap: "nowrap" }}>Job Priority</div>,
      dataIndex: "priority",
      key: "priority",
      align: "center",
      width: 30,
      filters: [
        {
          text: "5",
          value: "5"
        },
        {
          text: "9",
          value: "9"
        },
        {
          text: "2",
          value: "2"
        }
      ],
      onFilter(value, record) {
        return value === record.priority.toString();
      },
      render(value, record, index) {
        const isInternalUser = record.user.userType === "INTERNAL";
        const newPriority = value === 5 ? (isInternalUser ? 2 : 9) : 5;
        return (
          <>
            {value}{" "}
            {[0, 5].includes(record.status) && (
              <Popconfirm
                title={"Change Priority"}
                description={`Do you want to change the priority of the job to ${newPriority}?`}
                okText={"Yes"}
                cancelText={"No"}
                onConfirm={(e) => {
                  changePriority(record.id, newPriority);
                }}>
                <SwapOutlined style={{ marginLeft: 8 }} />
              </Popconfirm>
            )}
          </>
        );
      }
    },
    {
      title: <div style={{ textWrap: "nowrap" }}>Created Date</div>,
      width: 20,
      dataIndex: "createdAt",
      key: "createdAt",
      align: "center",
      defaultSortOrder: "descend",
      sorter: false,
      render(_, record: any) {
        return `${new Date(record.createdAt).toLocaleDateString()} ${new Date(
          record.createdAt
        ).toLocaleTimeString()}`;
      }
    },
    {
      title: "Executed/Estimated Run Time",
      dataIndex: "elapsedTime",
      key: "RunTime",
      width: 30,
      align: "center",
      render: (value, record) => {
        return <div>{record.status === 4 ? 0 : (Number(value) / 60).toFixed(2)} Min</div>;
      }
    },
    {
      title: "Percentage(%)",
      dataIndex: "jobCompletionPercentage",
      key: "jobCompletionPercentage",
      width: 20,
      align: "center",
      render: (value, record) => (
        <p>
          {record.status >= 4
            ? "0.00"
            : record.status === 0
              ? rescaleJobRunningPercentage(value?.toFixed(2))
              : value?.toFixed(2) || "0.00"}
        </p>
      )
    },
    {
      title: "Status",
      key: "status",
      width: 120,
      dataIndex: "status",
      align: "center",
      render: (value, record) => (
        <Badge
          size="small"
          style={{
            color: "#fff",
            fontSize: "12px",
            border: "1px solid #312E2E",
            borderRadius: "16px",
            height: "22px",
            paddingLeft: "8px",
            paddingRight: "8px",
            paddingTop: "8px",
            paddingBottom: "8px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginLeft: "auto",
            marginRight: "auto"
          }}
          color={STATUS_MAP_COLOR[value]}
          text={
            <span
              style={{
                fontSize: "12px",
                fontWeight: 500,
                color: STATUS_MAP_COLOR[value],
                textWrap: "nowrap"
              }}>
              {value === 2 && !record.result ? "UPLOADING RESULT" : STATUS_MAP[value as 0 | 1]}
            </span>
          }
        />
      )
    },
    {
      title: "(Expected) Completion Date",
      key: "completionDate",
      width: 120,
      dataIndex: "completionDate",
      align: "center",
      render(_, record: any) {
        return record.completionDate && record.status !== 3 // should not be cancelled
          ? `${new Date(record.completionDate).toLocaleDateString()} ${new Date(
              record.completionDate
            ).toLocaleTimeString()}`
          : null;
      }
    },
    {
      title: "Action",
      key: "action",
      dataIndex: "action",
      width: 140,
      align: "right",
      render: (_, record) => {
        return (
          <Flex gap={"12px"} align="center" justify="end">
            {record.status === JobStatus.COMPLETED && (
              <ViewJob
                jobId={record.id}
                jobName={record.job_name}
                elapsedTime={record.elapsedTime}
              />
            )}

            {record.status !== JobStatus.CANCELLED &&
              record.status !== JobStatus.COMPLETED &&
              record.status !== JobStatus.UNRUNNABLE && (
                <Popconfirm
                  title="Are you sure you want to cancel this job?"
                  okText={"Yes"}
                  cancelText={"No"}
                  onConfirm={async () => {
                    await cancel(record.id);
                  }}>
                  <Button type="text">Cancel Job</Button>
                </Popconfirm>
              )}

            {record.status === JobStatus.UNRUNNABLE && record.result}

            {record.status !== JobStatus.RUNNING && record.status !== JobStatus.IN_QUEUE && (
              <Popconfirm
                title="Are you sure you want to delete this job?"
                okText={"Yes"}
                cancelText={"No"}
                onConfirm={async () => {
                  await deleteJob(record.id);
                }}>
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  size="small"
                  style={{
                    color: "red",
                    padding: "4px",
                    display: "grid",
                    placeItems: "center"
                  }}
                />
              </Popconfirm>
            )}
          </Flex>
        );
      }
    }
  ];

  if (tableQueryResult.isLoading) return <LoadingSpinner />;

  return (
    <>
      <UserInfoModal
        show={userModal.show}
        onClose={() => {
          setUserModal({
            show: false,
            user: undefined
          });
        }}
        user={userModal.user}
      />
      {createJobModal && (
        <CreateJobModal
          isModalOpen={createJobModal}
          setModalOpen={(open) => {
            setCreateJobModal(open);
          }}
          fetchJobData={() => {
            invalidate({
              resource: "jobs/admin",
              invalidates: ["resourceAll"]
            });
            return Promise.resolve();
          }}
        />
      )}
      <div>
        <Typography.Title style={{ fontWeight: "normal", fontSize: "30px" }} level={1}>
          Jobs overview
        </Typography.Title>
        <SearchComponent
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          placeholder="Search By Job Name">
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginLeft: 8
            }}>
            <Button
              style={{ height: "40px", border: "none" }}
              onClick={() => {
                setCreateJobModal(true);
              }}
              icon={<PlusOutlined />}>
              Create Job
            </Button>
          </div>
        </SearchComponent>
        {jobId && (
          <Flex style={{ marginTop: "30px", marginBottom: 12 }}>
            <Tag
              closeIcon
              color={COLOR_PRIMARY_ORANGE}
              onClose={() => {
                setSearchParams("");
              }}>
              Job ID: {jobId}
            </Tag>
          </Flex>
        )}
        <Table
          style={{ ...(!jobId ? { marginTop: "30px" } : {}) }}
          {...tableProps}
          dataSource={tableProps.dataSource?.filter((data) => {
            return jobId
              ? data.id?.toString()?.toLowerCase()?.includes(jobId)
              : data.job_name.toLowerCase().includes(searchQuery.toLowerCase());
          })}
          columns={columns}
          // scroll={{ x: "max-content" }}
          pagination={getPaginationProps()}
        />
      </div>
    </>
  );
};
